import { ILoadingAnimationManager } from 'library/ts/managers/loading_animation/interfaces/ILoadingAnimationManager';
import { ILoadingAnimationManagerConfig } from 'library/ts/managers/loading_animation/interfaces/ILoadingAnimationManagerConfig';
import { LoadingAnimationManager } from 'library/ts/managers/loading_animation/LoadingAnimationManager';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { IPlayerAdapterEventVO } from 'library/ts/player/adapter/interfaces/events/IPlayerAdapterEventVO';
import { CSSValue } from 'library/ts/ui/enums/css/CSSValue';
import { getIconLoader } from 'library/ts/ui/svg/loaders';
import ResizeObserverPolyfill from 'resize-observer-polyfill/dist/ResizeObserver.es';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IResizeObserverEntry } from 'tag/ts/tag/interfaces/IResizeObserverEntry';
import { ITagView } from 'tag/ts/tag/interfaces/mvc/ITagView';
import { SkinModeResolver } from 'tag/ts/tag/model/config/SkinModeResolver';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { TagDomAttribute } from 'tag/ts/tag/model/type/TagDomAttribute';
import { TagUtilities } from 'tag/ts/tag/utilities/TagUtilities';
import { AdBlockerManager } from 'tag/ts/tag/view/AdBlockerManager';
import { AdClickOverlayManager } from 'tag/ts/tag/view/managers/AdClickOverlayManager';
import { ViewLibrary } from 'tag/ts/tag/view/ViewLibrary';

declare global {
	interface Window {
		ResizeObserver: any; // declare ResizeObserver global namespace
	}
}

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Tag View
 */
export class TagView extends AbstractTagClass implements ITagView {

	/**
	 * @description Set on `smartTagContentElement` (Tag's own internal skeleton node — never
	 * `#container`, the host-provided element) whenever Tag's own `LoadingAnimationManager` is
	 * actually constructed — a generic, Tag-core signal any consumer can key off of (e.g.
	 * `plugin_mobile_web_ui`'s style.scss suppresses avia-js-ui's native spinner only when this
	 * class is present, so a mobile-web host that also sets `SKIN.defaultSkin: true` doesn't lose
	 * both spinners at once). Deliberately not on `#container` — a host is free to restyle/reclass
	 * that element for its own purposes, which would silently strip this marker.
	 */
	static readonly #LOADING_ANIMATION_ACTIVE_CLASS = 'smart-tag-loading-animation-active';

	#container: HTMLElement;
	#loadingAnimationManager: ILoadingAnimationManager;

	// ad blocker manager instance
	#adBlockManager: AdBlockerManager;

	// ad click overlay manager instance
	#adClickOverlayManager: AdClickOverlayManager;

	// Container for skin class instance
	#skin: any;

	// ResizeObserver instance; stored so it can be disconnect()'d on tag reset
	#resizeObserver: ResizeObserver;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'TagView'
			}
		);

		// delay View creation to allow apioMetadata to get processed
		this.utils.Delay.delay().then(
			() => {
				// setup tag events handler
				this.tagEventsHandler(
					this.#onTagEvent,
					[
						TagEvent.ON_PLAYER_EVENT,
						TagEvent.ON_VIEW_READY,
						TagEvent.ON_API_RESET_TAG_REQUEST
					]
				);

				this.#createSmartTagSkeleton();

				// setup resize observer only when needed
				if ( this?.params?.SYSTEM?.embedType !== EmbedType.HTML5 ) {
					this.#setupResizeObserver();
				}

				// inform tag application
				this.dispatchTagEvent(
					new TagEventVO(
						TagEvent.ON_VIEW_READY,
						this.tag,
						undefined,
						this,
						this.name
					)
				);
			}
		);
	}

	/**
	 * @description Returns reference to SmartTag container element, the one with attribute smart-tag-parent='uuid'
	 * @return {HTMLElement}
	 */
	get container(): HTMLElement {
		return this.#container;
	}

	/**
	 * @description Returns Smart Tag skeleton DOM elements as compound object
	 * @returns {{smartTag: HTMLElement, smartTagContent: HTMLElement, smartTagOverlay: HTMLElement, smartTagLoadingAnimation: HTMLElement, smartTagDiagnostic: HTMLElement}}
	 */
	get smartTagDOM(): {
		smartTag: HTMLElement;
		smartTagContent: HTMLElement;
		smartTagOverlay: HTMLElement;
		smartTagLoadingAnimation: HTMLElement;
		smartTagDiagnostic: HTMLElement;
		} {
		return {
			smartTag: this.container.querySelector( `[${TagDomAttribute.SMART_TAG}="${this.uuid}"]` ),
			smartTagContent: this.container.querySelector( `[${TagDomAttribute.CONTENT}="${this.uuid}"]` ),
			smartTagOverlay: this.container.querySelector( `[${TagDomAttribute.OVERLAY}="${this.uuid}"]` ),
			smartTagLoadingAnimation: this.container.querySelector( `[${TagDomAttribute.LOADING_ANIMATION}="${this.uuid}"]` ),
			smartTagDiagnostic: this.container.querySelector( `[${TagDomAttribute.DIAGNOSTIC}="${this.uuid}"]` )
		};
	}

	/**
	 * @description Returns reference to root Tag DOM container element, the one with attribute smart-tag-content='uuid'
	 * @return {HTMLElement}
	 */
	get smartTagContentElement(): HTMLElement {
		return this.container.querySelector( `[${TagDomAttribute.CONTENT}="${this.uuid}"]` );
	}

	/**
	 * @description Returns reference to Tag DOM container element, the one with attribute smart-tag='uuid'
	 * @return {HTMLElement}
	 */
	get smartTagElement(): HTMLElement {
		return this.container.querySelector( `[${TagDomAttribute.SMART_TAG}="${this.uuid}"]` );
	}

	/**
	 * @description Returns reference to Tag DOM container element, the one with attribute smart-tag-overlay='uuid'
	 * @return {HTMLElement}
	 */
	get smartTagOverlayElement(): HTMLElement {
		return this.container.querySelector( `[${TagDomAttribute.OVERLAY}="${this.uuid}"]` );
	}

	/**
	 * @description Returns reference to Tag DOM diagnostic overlay element, the one with attribute smart-tag-diagnostic='uuid'
	 * @return {HTMLElement}
	 */
	get diagnosticElement(): HTMLElement {
		return this.container.querySelector( `[${TagDomAttribute.DIAGNOSTIC}="${this.uuid}"]` );
	}

	/**
	 * @description Returning instance to loading animation manager
	 */
	get loadingAnimationManager() {
		return this.#loadingAnimationManager;
	}

	/**
	 * @description Player type aka Chromeless / Full. Delegates to
	 * `SkinModeResolver.resolvePlayerTypeFromDeviceSignals()` so this and `SkinModeResolver`'s
	 * own computed-default tier share one implementation and can never drift apart — see that
	 * method's doc for why it deliberately never looks at `SKIN.enabled`.
	 */
	get playerType() {
		return SkinModeResolver.resolvePlayerTypeFromDeviceSignals(
			{
				embedType: this.params.SYSTEM.embedType,
				isDesktopCapability: this.capabilities.isDesktop,
				defaultSkin: this.params.SKIN.defaultSkin === true
			}
		);
	}

	get skin(): any {
		return this.#skin;
	}

	set skin( value: any ) {
		this.#skin = value;
	}

	/**
	 * @description Hides the loading animation background image
	 */
	#disableLoadingAnimationBackgroundImage = (): void => {
		if ( this.utils.Type.isFunction( this.loadingAnimationManager?.disableBackgroundImage ) ) {
			this.loadingAnimationManager.disableBackgroundImage();
		}
	};

	#setLoadingAnimationBackgroundImage = ( imageURL: string ): void => {
		if ( this.utils.Type.isFunction( this.loadingAnimationManager?.setBackgroundImage ) ) {
			this.loadingAnimationManager.setBackgroundImage( imageURL );
		}
	};

	#disableLoadingAnimationFadeIn = (): void => {
		if ( this.utils.Type.isFunction( this.loadingAnimationManager?.disableFadeIn ) ) {
			this.loadingAnimationManager.disableFadeIn();
		}
	};

	/**
	 * @description Creates Tag HTML skeleton
	 */
	#createSmartTagSkeleton = (): void => {
		this.#container = this.tag.params.TAG.container as HTMLElement;
		// set attribute on parent element for identification purposes
		this.#container.setAttribute( TagDomAttribute.SMART_TAG_PARENT, this.uuid );
		// create + append smart tag skeleton
		this.#container.appendChild(
			this.utils.DOM.stringToElement(
				ViewLibrary.SMART_TAG_SKELETON.replace( /\$UUID\$/g, this.uuid )
			)
		);

		this.#setSmartTagDOMSkeletonBackground();
		// Create and set loading animation only if Tag loading animation is enabled and we are not using avia-js-ui
		const loadingAnimationEnabled = this?.params?.TAG?.loadingAnimation === true && this?.params?.SKIN?.defaultSkin === false;
		if ( loadingAnimationEnabled ) {
			this.#setLoadingAnimation();
		}
		// toggle() rather than only ever add() — a Tag instance that doesn't build its own
		// spinner must explicitly clear this, not just leave it unset, otherwise a container
		// reused across a reset/recreate cycle keeps the previous instance's stale class (see
		// ON_API_RESET_TAG_REQUEST below for the other half of this cleanup)
		//
		// Applied to smartTagContentElement (Tag's own internal skeleton node), not #container —
		// #container is the host-provided element (TAG.container), which a host is free to
		// restyle/reclass for its own purposes (e.g. an orientation toggle doing a raw
		// `element.className = ...` overwrite). That would silently wipe this marker class if it
		// lived there, permanently un-suppressing avia-js-ui's native waiting overlay for the rest
		// of the session with no signal that anything broke (found via PLPSCORE-389 mobile web
		// test harness investigation, 2026-08-05).
		this.smartTagContentElement?.classList.toggle( TagView.#LOADING_ANIMATION_ACTIVE_CLASS, loadingAnimationEnabled );
	};

	/**
	 * @description Sets up loading animation for all platforms + background image for desktop
	 */
	#setLoadingAnimation = (): void => {
		const baseConfig: ILoadingAnimationManagerConfig = this?.params?.TAG?.loadingAnimationConfig;
		const brand = TagUtilities.getBrand();
		const iconLoaderSvg = getIconLoader( brand );
		const loadingAnimationTagConfig: ILoadingAnimationManagerConfig = {
			...baseConfig,
			accessibility: {
				...baseConfig?.accessibility,
				// SkinView.#syncLoadingLiveRegionMessage() applies host override after Skin mounts.
				loadingMessage: baseConfig?.accessibility?.loadingMessage
					?? ( this.tag?.model?.config?.skin?.Aria?.params?.Aria as { LOADING?: { MESSAGE?: { ariaLabel?: string } } } | undefined )
						?.LOADING?.MESSAGE?.ariaLabel
					?? 'Loading'
			},
			spinner: {
				...baseConfig?.spinner,
				customSvg: iconLoaderSvg
			}
		};

		this.#loadingAnimationManager = new LoadingAnimationManager(
			'smart-tag-loading-animation',
			(
				this.tag.params.TAG.container as HTMLElement ).querySelector(
				`[${TagDomAttribute.LOADING_ANIMATION}=${this.tag.uuid}]`
			),
			this.utils,
			this.capabilities,
			this?.params?.TAG?.loadingAnimation,
			loadingAnimationTagConfig
		);

		// only set up background image for DESKTOP (HTML5 does not have access to apiMetadata until VideoAPI)

		if (
			loadingAnimationTagConfig?.backgroundImage?.enabled
			&&
			( this?.params?.SYSTEM?.embedType === EmbedType.DESKTOP
				||
				this?.params?.SYSTEM?.embedType === EmbedType.MOBILE_WEB )
		) {
			// disable background image for live content
			if ( this?.apiMetadata?.isLive ) {
				this.#disableLoadingAnimationBackgroundImage();
			} else {
				// set background image using apiMetadata if it is not provided in params.TAG
				if (
					!this.utils.URL.valid( loadingAnimationTagConfig?.backgroundImage?.imageURL )
					&&
					this.utils.URL.valid( this?.apiMetadata?.thumbnail )
				) {
					this.#setLoadingAnimationBackgroundImage( this?.apiMetadata?.thumbnail );
				}
			}
		}

		this.utils.Delay.delay( undefined )
			.then(
				() => {
					if ( this.utils.Type.isFunction( this?.loadingAnimationManager?.show ) ) {
						this.loadingAnimationManager.show();
					}
				}
			);
	};

	/**
	 * @description Sets background color on Smart Tag skeleton DOM elements
	 */
	#setSmartTagDOMSkeletonBackground = (): void => {
		// create an array of our dom objects to process
		const domObjectsToProcess = [
			{
				element: this.smartTagElement,
				color: this?.params?.TAG?.background?.smartTag?.color
			},
			{
				element: this.smartTagContentElement,
				color: this?.params?.TAG?.background?.content?.color
			},
			{
				element: this.smartTagOverlayElement,
				color: this?.params?.TAG?.background?.overlay?.color
			}
		];
		// and process it...
		domObjectsToProcess.forEach(
			( obj ) => {
				// if color for dom element is not empty and is a string
				if ( !this.utils.Type.isEmpty( obj.color ) && this.utils.Type.isString( obj.color ) ) {
					// and if dom element exists
					if ( this.utils.Type.isElement( obj.element ) ) {
						// set background color
						obj.element.style.removeProperty( 'background-color' );
						obj.element.style.setProperty( 'background-color', obj.color.trim(), CSSValue.IMPORTANT );
					}
				}
			}
		);
	};

	/**
	 * @description ResizeObserver event handler
	 * @param {IResizeObserverEntry} event
	 */
	#onResizeObserverEvent = ( event: IResizeObserverEntry ): void => {
		// dispatch resize event to this Tag application
		this.dispatchTagEvent(
			this.getTagEventVO(
				TagEvent.ON_RESIZE,
				this.tag,
				{
					resize: event
				},
				this,
				this.name
			)
		);
	};

	/**
	 * @description Setup ResizeObserver on SmartTag container element
	 */
	#setupResizeObserver = (): void => {
		const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;

		/**
		 * @description Setup instance of ResizeObserver
		 * @type {ResizeObserver}
		 */
		this.#resizeObserver = new ResizeObserver(
			( entries, observer ) => {
				for ( let i = 0; i < entries.length; i++ ) {
					const entry = entries[ i ];
					const target = entry.target;
					const rect = entry.contentRect;
					setTimeout( this.#onResizeObserverEvent, undefined, entry ); // execute resize callback
				}
			}
		);

		// setup resize listener
		this.#resizeObserver.observe(
			this.#container.querySelector(
				`[${TagDomAttribute.SMART_TAG}=${this.uuid}]`
			)
		);
	};

	/**
	 * @description Player event handler
	 * @param {IPlayerAdapterEventVO} event
	 */
	#onPlayerEvent = ( event: IPlayerAdapterEventVO ): void => {

		const showLoadingAnimation = (): void => {
			// re-show during buffering/seek on any PlayerType.FULL (avia-js-ui) session too, not
			// just desktop — mobile web's own native avia spinner is suppressed via CSS in favor
			// of this one, for a uniform loading animation across platforms.
			if ( this.utils.Type.isFunction( this.loadingAnimationManager?.show ) ) {
				this.loadingAnimationManager.backgroundOpacity( 0 );
				this.loadingAnimationManager.show();
			}
		};

		const hideLoadingAnimation = (): void => {
			if ( this.utils.Type.isFunction( this.loadingAnimationManager?.hide ) ) {
				this.loadingAnimationManager.hide();
			}
		};

		// @SONAR_START_NO_SCAN@
		switch ( event?.type ) {
			case PlayerAdapterEvent.AD_START:
			case PlayerAdapterEvent.AD_PLAYING:
			case PlayerAdapterEvent.AD_PROGRESS:
			case PlayerAdapterEvent.CONTENT_START:
				// background image and fade in animation should only apear on initial load
				this.#disableLoadingAnimationBackgroundImage();
				this.#disableLoadingAnimationFadeIn();
			// intentional fall-through to run hideLoadingAnimation()
			case PlayerAdapterEvent.CONTENT_PLAYING:
			case PlayerAdapterEvent.CONTENT_PROGRESS:
				hideLoadingAnimation();
				break;
			case PlayerAdapterEvent.AD_BREAK_COMPLETE:
			case PlayerAdapterEvent.RESOURCE_BUFFERING:
			case PlayerAdapterEvent.SEEK_START:
				showLoadingAnimation();
				break;
		}
		// @SONAR_END_NO_SCAN@
	};

	/**
	 * @description Tag application event handler
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		switch ( event?.type ) {

			case TagEvent.ON_VIEW_READY:
				// remove view ready event listener
				this.removeTagEventsHandler( this.#onTagEvent, this?.tag?.eventDispatcher, [ TagEvent.ON_VIEW_READY ] ).then(
					( data ) => {
						// instantiate ad blocker manager on non-HTML5 devices
						if ( this.params.SYSTEM.embedType !== EmbedType.HTML5 ) {
							this.#adBlockManager = new AdBlockerManager( { tag: this.tag } );
						}

						if (
							this?.params?.SYSTEM?.embedType !== EmbedType.HTML5 &&
							this?.params?.SKIN?.defaultSkin !== true
						) {
							const getContainer = (): HTMLElement => {
								return this.smartTagContentElement;
							};

							if ( this.utils.Type.isElement( this.smartTagContentElement ) ) {
								this.#adClickOverlayManager = new AdClickOverlayManager( { tag: this.tag, getContainer } );
							} else {
								this.logger?.warn( 'AdClickOverlayManager: smartTagContentElement not available' );
							}
						}
					}
				);
				break;

			case TagEvent.ON_PLAYER_EVENT:
				this.#onPlayerEvent( event?.data?.playerAdapterEvent );
				break;

			case TagEvent.ON_API_RESET_TAG_REQUEST:
				// Disconnect the ResizeObserver on tag reset; it observes the SmartTag container
				// and would otherwise leak across reset/recreate cycles.
				this.#resizeObserver?.disconnect();
				// Same leak risk for the loading-animation marker class — clear proactively here
				// rather than waiting for the next instance's own (delayed, async) skeleton
				// creation to correct it, closing the window where a recreated Tag's
				// avia-waiting-overlay could be wrongly suppressed by the previous instance's
				// stale class.
				this.smartTagContentElement?.classList.remove( TagView.#LOADING_ANIMATION_ACTIVE_CLASS );
				break;
		}
	};

}